import * as React from 'react'

import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import {
  IPermitTypeField,
  IPreciseLocation,
  ISiteLocation,
  ISitePermit,
  PermitFieldType,
} from '~/client/graph'
import CalendarPlaceholderStore from '~/client/src/shared/components/CalendarDayView/components/CalendarPlaceholder.store'
import MapAnchoredCardController from '~/client/src/shared/components/MapAnchoredCard/MapAnchoredCardController'
import {
  measureFirstInputCenter,
  WORKFLOW_PIN_CARD_ANCHOR,
} from '~/client/src/shared/components/MapAnchoredCard/workflowPinCardAnchor'
import { preciseLocationAtCoordinates } from '~/client/src/shared/components/MapBoxViewer/preciseLocationAtCoordinates'
import {
  captureMapScreenshots,
  CAPTURE_FAILED_MESSAGE,
  CAPTURE_HEIGHT,
  CAPTURE_WIDTH,
} from '~/client/src/shared/components/SitePermitCreationForm/components/PermitFormFields/components/captureMapScreenshots'
import SitePermitCreationFormStore from '~/client/src/shared/components/SitePermitCreationForm/WorkflowCreationForm.store'
import {
  companyPermitFieldTypes,
  locationPermitFieldTypes,
  materialPermitFieldTypes,
  userPermitFieldTypes,
} from '~/client/src/shared/constants/PermitFieldTypeConstants'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { preciseLocationZoom } from '~/client/src/shared/map/mapZoomRule'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'
import GraphExecutorStore from '~/client/src/shared/stores/domain/GraphExecutor.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MarkupsStore from '~/client/src/shared/stores/domain/Markups.store'
import MaterialCategoriesStore from '~/client/src/shared/stores/domain/MaterialCategories.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import PermitInspectionChangesStore from '~/client/src/shared/stores/domain/PermitInspectionChanges.store'
import PermitInspectionsStore from '~/client/src/shared/stores/domain/PermitInspections.store'
import PermitStatusChangesStore from '~/client/src/shared/stores/domain/PermitStatusChanges.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import RecurringWorkflowSettingsStore from '~/client/src/shared/stores/domain/RecurringWorkflowSettings.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { createGripDrag } from '~/client/src/shared/utils/createGripDrag'
import Guard from '~/client/src/shared/utils/Guard'
import { isEditableOrControlTarget } from '~/client/src/shared/utils/isEditableOrControlTarget'
import { isBaseSurfaceActive } from '~/client/src/shared/utils/keyboardClaim'
import {
  clearSnapshotUrls,
  hasPreciseCoordinates,
  mergeSnapshotResult,
} from '~/client/src/shared/utils/locationSnapshot'
import { toast } from '~/client/src/shared/utils/toast'
import {
  applyWorkflowTypeSwitchCarry,
  IWorkflowDateSnapshot,
  planWorkflowTypeSwitchCarry,
} from '~/client/src/shared/utils/workflowTypeSwitchCarry'

import type MapMarkupSessionStore from '../LogisticsMapView/MapMarkupSession.store'
import {
  COMPOSER_ROW_TYPES,
  getMapCreatableWorkflowTypes,
  isComposerCompletable,
} from '../LogisticsMapView/workflowCreationPolicy'
import {
  additionalDateMapper,
  companyMapper,
  materialMapper,
  measureMapper,
  stringMapper,
  userMapper,
} from '../SitePermitCreationForm/utils/changedFieldsMessageMappers'

// One editable line in the card. Everything comes off the template so a rename or
// reorder in the Workflow Configurator lands here with no code change.
export interface IComposerRow {
  id: string
  type: PermitFieldType
  caption: string
  isRequired: boolean
  value: string
  open: (anchor: HTMLElement) => void
}

export interface IWorkflowComposerDeps {
  eventsStore: EventsStore
  projectDateStore: ProjectDateStore
  fileUploadingStore: FileUploadingStore
  permitTypesStore: WorkflowTypesStore
  userProjectsStore: UserProjectsStore
  projectMembersStore: ProjectMembersStore
  companiesStore: CompaniesStore
  locationAttributesStore: LocationAttributesStore
  materialsStore: MaterialsStore
  materialCategoryStore: MaterialCategoriesStore
  tagsStore: TagsStore
  graphExecutorStore: GraphExecutorStore
  closuresStore: ClosuresStore
  markupsStore: MarkupsStore
  permitStatusChangesStore?: PermitStatusChangesStore
  permitInspectionsStore?: PermitInspectionsStore
  permitInspectionChangesStore?: PermitInspectionChangesStore
  calendarPlaceholderStore?: CalendarPlaceholderStore
  recurringWorkflowSettingsStore?: RecurringWorkflowSettingsStore
  sitemapItemsStore?: SitemapItemsStore
}

// Desktop only uses expanded⇄collapsed; mobile adds the intermediate 'peek'.
export type ComposerSheetSnap = 'expanded' | 'peek' | 'collapsed'

// Clearance below the card so the docked toolbar (gap + row + margin) stays on-screen.
const DOCKED_TOOLBAR_RESERVE_PX = 72
// Gap between the card's bottom and the docked toolbar (Figma).
const DOCKED_TOOLBAR_GAP_PX = 8
// Minimized cluster's default bottom inset (= the class fallback 0.75rem).
const DOCKED_TOOLBAR_BOTTOM_PX = 12
// Keep the dragged docked cluster this far inside the viewport edges.
const DOCKED_TOOLBAR_MARGIN_PX = 8

// Owns a headless SitePermitCreationFormStore (reused validation/gates, no form UI);
// markups buffer under a temp id, flushed to the real id on create (STX1-686).
export default class WorkflowComposerStore {
  @observable public isSubmitting: boolean = false
  // The store survives collapse so the form restores intact on maximize.
  @observable public snap: ComposerSheetSnap = 'expanded'
  // True for any non-expanded snap; desktop never enters 'peek', so it stays 2-state there.
  @computed
  public get isMinimized(): boolean {
    return this.snap !== 'expanded'
  }
  // Did a markup tool auto-collapse the form (vs a deliberate minimize)? Desktop re-expands
  // on edit-end only for auto-collapse; a deliberate minimize stays docked (continuous drawing).
  @observable public autoMinimized: boolean = false
  // Rect of the field row that opened the active picker; desktop anchors the popover under it.
  @observable.ref public pickerAnchor: DOMRect = null
  public readonly formStore: SitePermitCreationFormStore
  // Glues the desktop card to the workflow pin (STX1-686 spec offsets). Session
  // popups deliberately OVERLAP the default map chrome (clusters/pill) — they sit
  // above it in z-order and the user can drag them aside; no collision logic.
  public readonly anchorController = new MapAnchoredCardController({
    getMap: () => this.session.mapBoxViewerStore.mapRef?.getMap(),
    onMapChange: cb => this.session.mapBoxViewerStore.onMapChange(cb),
    getCoordinates: () => this.session.creationCoordinates,
    anchor: WORKFLOW_PIN_CARD_ANCHOR,
    measureAlignOffset: measureFirstInputCenter,
    // Reserve only while expanded (toolbar docks under the card); minimized/editing
    // sends the toolbar to the bottom of the screen, so no card clearance is needed.
    getBottomReservePx: () =>
      this.isMinimized ? 0 : DOCKED_TOOLBAR_RESERVE_PX,
    // Same frame as the card move, reusing the card box — no extra DOM reads while panning.
    onReproject: box => this.syncDockedToolbar(box),
  })

  public constructor(
    private readonly session: MapMarkupSessionStore,
    private readonly deps: IWorkflowComposerDeps,
    private readonly onRequestClose: () => void,
    private readonly onRouteToForm: () => void,
    // Scopes the docked-cluster lookup to this overlay subtree so a foreign
    // [data-docked-cluster] (the precise-location editor stage) can't be latched.
    private readonly getClusterScope?: () => ParentNode | null,
  ) {
    makeObservable(this)
    // inject() forces every store prop optional, so a mistyped provider key arrives as
    // undefined and only crashes deep in the form store. Fail here, naming the key.
    Guard.requireAll({
      eventsStore: deps.eventsStore,
      projectDateStore: deps.projectDateStore,
      fileUploadingStore: deps.fileUploadingStore,
      permitTypesStore: deps.permitTypesStore,
      userProjectsStore: deps.userProjectsStore,
      projectMembersStore: deps.projectMembersStore,
      companiesStore: deps.companiesStore,
      locationAttributesStore: deps.locationAttributesStore,
      materialsStore: deps.materialsStore,
      materialCategoryStore: deps.materialCategoryStore,
      tagsStore: deps.tagsStore,
      graphExecutorStore: deps.graphExecutorStore,
      closuresStore: deps.closuresStore,
      markupsStore: deps.markupsStore,
    })
    this.formStore = new SitePermitCreationFormStore(
      deps.eventsStore,
      deps.projectDateStore,
      deps.fileUploadingStore,
      deps.permitTypesStore,
      deps.userProjectsStore,
      deps.projectMembersStore,
      deps.companiesStore,
      deps.locationAttributesStore,
      deps.materialsStore,
      deps.materialCategoryStore,
      deps.tagsStore,
      deps.graphExecutorStore,
      deps.closuresStore,
      deps.markupsStore,
      null,
      deps.permitStatusChangesStore,
      deps.permitInspectionsStore,
      deps.permitInspectionChangesStore,
      deps.calendarPlaceholderStore,
      deps.recurringWorkflowSettingsStore,
      deps.sitemapItemsStore,
    )
    this.formStore.onCreateSuccess = this.handleCreateSuccess
    this.formStore.onCreateError = this.handleCreateError
    this.formStore.onTypeChangeRequested = this.handleTypeChangeRequested
    this.initDefaults()
    this.session.onCoordinatesChange = this.handleCoordinatesChange
    window.addEventListener('keydown', this.handleKeyDown)
  }

  public dispose(): void {
    this.isDisposed = true
    this.unregisterCapture()
    this.formStore.onTypeChangeRequested = null
    window.removeEventListener('keydown', this.handleKeyDown)
    this.session.onCoordinatesChange = null
    this.anchorController.stopAnchoring()
    this.dockDrag.stop()
  }

  // Derived, not latched: a re-seed swaps the template, and three observers read this.
  @computed
  public get workflowType(): WorkflowType {
    return this.formStore.template
  }

  @computed
  public get offerableWorkflowTypes(): WorkflowType[] {
    return getMapCreatableWorkflowTypes(this.deps.permitTypesStore)
  }

  @computed
  public get offerableWorkflowTypeIds(): string[] {
    return this.offerableWorkflowTypes.map(type => type.id)
  }

  @computed
  public get canChangeWorkflowType(): boolean {
    return this.offerableWorkflowTypes.length > 1
  }

  @action.bound
  public openTypePicker(anchor: HTMLElement): void {
    this.setPickerAnchor(anchor)
    this.formStore.showFormTypeSelector()
  }

  /** Claims the pick: the form's own re-seed passes no location and would drop the pin. */
  @action.bound
  private handleTypeChangeRequested(typeId: string): boolean {
    if (!typeId || typeId === this.workflowType?.id) {
      return true
    }
    this.applyWorkflowType(typeId)
    return true
  }

  private applyWorkflowType(typeId: string): void {
    const next = this.deps.permitTypesStore.getPermitTypeById(typeId)
    // Planned before the re-seed, which rebuilds editablePermit in place.
    const plan = planWorkflowTypeSwitchCarry(
      this.formStore.editablePermit,
      next,
      this.seedDates,
    )
    // Set before either branch: the hand-off reads the type off the session.
    this.session.setCreationWorkflowType(typeId)
    if (!isComposerCompletable(next)) {
      // Same channel the markup buffer travels on; the form consumes it once.
      this.deps.eventsStore.appState.preSelectedWorkflowCarryPlan = plan
      this.onRouteToForm?.()
      return
    }
    // Claiming the pick skips onPermitTypeChanged's reset of the old type's blocks.
    this.formStore.inspectionFieldsStore.deselectInspection()
    this.formStore.hideNonWorkTimeLabel()
    this.formStore.unblockSubmitButton()
    this.initDefaults()
    applyWorkflowTypeSwitchCarry(
      this.formStore.editablePermit,
      next,
      plan,
      this.formStore.workflowFieldsStore,
    )
    // initDefaults snapped before the carried dates and company landed.
    this.formStore.resnapSeededPin()
    // initDefaults checked the new type's default dates, not the carried ones.
    if (plan.dates) {
      this.formStore.openDeadlineModalIfNeed()
    }
  }

  // isChanged can't tell user edits on a creation, so carried dates are diffed against this.
  private seedDates: IWorkflowDateSnapshot = null

  // Set by dispose(): the host unmounted, so nothing can render a resumed draft.
  private isDisposed: boolean = false
  private captureRegistryKey: string | null = null

  // Pin reposition touches ONLY the location field — the user's
  // title/company/responsible/date must survive.
  private handleCoordinatesChange = (): void => {
    const coords = this.session.creationCoordinates
    if (coords) {
      this.formStore.applyPreciseLocationFromMap({
        ...preciseLocationAtCoordinates(this.session.mapBoxViewerStore, coords),
        // A pin move changes where, not which way round: keep the drop camera.
        ...(this.session.creationCamera ?? {}),
      } as IPreciseLocation)
    }
    this.anchorController.reproject()
  }

  @computed
  public get canSubmit(): boolean {
    return !this.isSubmitting && this.formStore.canSaveWorkflow
  }

  // True once the user leaves idle Select; the sheet/card collapses on this so
  // the map clears for drawing.
  @computed
  public get isMarkupEngaged(): boolean {
    return !this.session.isEditorIdle
  }

  @computed
  public get invalidFieldCaptions(): string[] {
    return (this.formStore.workflowFieldsStore.fields || [])
      .filter(field => !field.isValidIgnored && !field.isValid)
      .map(field => field.caption)
      .filter(Boolean)
  }

  public get titleValue(): string {
    return this.formStore.editablePermit?.title || ''
  }

  // Mandatority, not "blocks submit": titleField.isValid exempts admins/forms-masters,
  // so they see the asterisk with Submit enabled — same as the mandatory rows.
  @computed
  public get isTitleRequired(): boolean {
    return !!this.formStore.workflowFieldsStore.titleField.isRequired
  }

  // Same formatter as the form's date field (PermitFormStartEndDatesField) so the
  // two can't drift: weekday + day + time range, widened for cross-day/cross-year.
  @computed
  public get whenLabel(): string {
    const permit = this.formStore.editablePermit
    if (!permit?.startDate) {
      return ''
    }
    return permit.getDateAndTimeToDisplay(this.deps.projectDateStore)
  }

  // Template array order is authoritative — the Configurator reorders by rewriting
  // that array — and availableWorkflowSteps is the same projection the full form and
  // the submit gate read, so the card can never disagree with canSubmit.
  @computed
  public get composerRows(): IComposerRow[] {
    const [step] =
      this.formStore.workflowFieldsStore.availableWorkflowSteps ?? []
    return (step?.fields ?? [])
      .filter(field => COMPOSER_ROW_TYPES.has(field.type))
      .map(field => ({
        id: field.id,
        type: field.type,
        caption: field.caption,
        isRequired: !!field.isMandatory,
        value: this.rowValue(field),
        open: (anchor: HTMLElement) => this.openFieldPicker(field, anchor),
      }))
  }

  // Reuses the form's own value mappers, so a row reads the same as the full form.
  private rowValue(field: IPermitTypeField): string {
    const { type } = field
    const {
      companiesStore,
      projectMembersStore,
      materialsStore,
      projectDateStore,
    } = this.deps

    if (type === PermitFieldType.StartFinishDates) {
      return this.whenLabel
    }
    if (locationPermitFieldTypes.includes(type)) {
      return this.collapse(
        this.getFieldValues<ISiteLocation>(field).map(location =>
          this.formStore.getLocationName(location),
        ),
      )
    }
    if (companyPermitFieldTypes.includes(type)) {
      return this.collapse(
        this.getFieldValues<string>(field).map(id =>
          companyMapper(id, companiesStore),
        ),
      )
    }
    if (userPermitFieldTypes.includes(type)) {
      return this.collapse(
        this.getFieldValues<string>(field).map(id =>
          userMapper(id, projectMembersStore),
        ),
      )
    }
    if (materialPermitFieldTypes.includes(type)) {
      return this.collapse(
        this.getFieldValues<Parameters<typeof materialMapper>[0]>(field).map(
          material => materialMapper(material, materialsStore),
        ),
      )
    }
    if (type === PermitFieldType.Measure) {
      return this.collapse(
        this.getFieldValues<Parameters<typeof measureMapper>[0]>(field).map(
          measureMapper,
        ),
      )
    }
    if (type === PermitFieldType.AdditionalDate) {
      return this.collapse(
        this.getFieldValues<number>(field)
          // An unset date is seeded as 0, which would format as 1970.
          .filter(Boolean)
          .map(date => additionalDateMapper(date, projectDateStore)),
      )
    }
    return this.collapse(this.getFieldValues<string>(field).map(stringMapper))
  }

  // A row is one line: show the first value and count the rest.
  private collapse(values: string[]): string {
    const present = values.filter(Boolean)
    if (!present.length) {
      return ''
    }
    return present.length === 1
      ? present[0]
      : `${present[0]} +${present.length - 1}`
  }

  @action.bound
  public setTitle(title: string): void {
    if (this.formStore.editablePermit) {
      this.formStore.editablePermit.title = title
    }
  }

  @action.bound
  public close(): void {
    this.onRequestClose()
  }

  // Deliberate user minimize (Minimize button / Esc): stays in the Dock — no auto-restore.
  @action.bound
  public minimize(): void {
    this.autoMinimized = false
    this.setSnap('collapsed')
  }

  // Automatic collapse when a markup tool engages; flagged so it re-expands once editing ends.
  @action.bound
  public autoMinimize(): void {
    this.autoMinimized = true
    this.setSnap('collapsed')
  }

  @action.bound
  public maximize(): void {
    this.autoMinimized = false
    this.setSnap('expanded')
  }

  // Mobile intermediate: keeps the creation context visible while the map clears for drawing.
  @action.bound
  public peekSheet(): void {
    this.setSnap('peek')
  }

  @action.bound
  public setSnap(next: ComposerSheetSnap): void {
    this.snap = next
    this.anchorController.reproject()
  }

  // Re-opening the form finalizes drawing (deselect + Select tool). An explicit
  // collapse must NOT call this — it keeps the drawing session intact.
  public exitDrawing(): void {
    this.session.exitDrawing()
  }

  // Opening the pill re-expands the card AND leaves edit mode (back to Select).
  @action.bound
  public openWorkflowForm(): void {
    this.maximize()
    this.exitDrawing()
  }

  // Last-written vars, so the per-frame reproject only touches the DOM on change.
  private dockedToolbarKey = ''

  // Cached — resolved per reproject frame; re-queried only after a remount detached it.
  private dockedClusterCache: HTMLElement = null
  private get dockedCluster(): HTMLElement | null {
    if (!this.dockedClusterCache?.isConnected) {
      const scope = this.getClusterScope?.() ?? document
      const el = scope.querySelector<HTMLElement>('[data-docked-cluster]')
      if (el !== this.dockedClusterCache) {
        // A fresh element (remounts across Move-Pin mode) carries no inline
        // position — drop the memo or the next sync would skip the write.
        this.dockedToolbarKey = ''
      }
      this.dockedClusterCache = el
    }
    return this.dockedClusterCache
  }

  // Inline styles on the cluster (they override its var-based class defaults) — NEVER
  // CSS vars on documentElement: a root custom-property write per camera frame
  // invalidates style for the entire document (measured: 2× frame time while panning).
  // Expanded: dock centred under the card. Minimized (idle OR editing): the toolbar +
  // its "Open workflow" button dock at the screen bottom, draggable via the grip.
  private syncDockedToolbar(box: {
    x: number
    y: number
    width: number
    height: number
  }): void {
    if (this.isMinimized) {
      this.writeDockedPosition()
      return
    }
    // Resolve the element FIRST — a remount resets the key memo (see dockedCluster).
    const el = this.dockedCluster
    if (!el) {
      return
    }
    const top = Math.round(box.y + box.height + DOCKED_TOOLBAR_GAP_PX)
    const left = Math.round(box.x + box.width / 2)
    const key = `card|${top}|${left}`
    if (key === this.dockedToolbarKey) {
      return
    }
    this.dockedToolbarKey = key
    el.style.top = `${top}px`
    el.style.bottom = 'auto'
    el.style.left = `${left}px`
    // Tailwind v4 centres via the CSS `translate` property — override that, not
    // `transform`, which would stack a second -50%.
    el.style.translate = '-50%'
  }

  // Bottom-centre of the screen + the user's drag offset (default {0,0} = exact
  // bottom-centre, matching the Figma dock).
  private writeDockedPosition(): void {
    // Resolve the element FIRST — a remount resets the key memo (see dockedCluster).
    const el = this.dockedCluster
    if (!el) {
      return
    }
    const { x, y } = this.dockedOffset
    const key = `dock|${x}|${y}`
    if (key === this.dockedToolbarKey) {
      return
    }
    this.dockedToolbarKey = key
    el.style.top = 'auto'
    el.style.bottom = `${DOCKED_TOOLBAR_BOTTOM_PX - y}px`
    el.style.left = `calc(50% + ${x}px)`
    el.style.translate = '-50%'
  }

  public clearDockedToolbar(): void {
    if (this.dockedToolbarKey === '') {
      return
    }
    this.dockedToolbarKey = ''
    const el = this.dockedClusterCache
    if (!el?.isConnected) {
      return
    }
    // Back to the class defaults (bottom-centre / the Precise-Location var).
    el.style.top = ''
    el.style.bottom = ''
    el.style.left = ''
    el.style.translate = ''
  }

  // Drag offset of the bottom-docked cluster; plain field — position flows through
  // inline styles, never React. Fresh per creation (per store).
  private dockedOffset = { x: 0, y: 0 }
  private dockedDragStart: {
    px: number
    py: number
    ox: number
    oy: number
  } = null
  private dockedDragCluster: HTMLElement = null

  // Docked-cluster grip: absolute-from-start delta, re-clamped each move (vs the card's
  // incremental re-base).
  private readonly dockDrag = createGripDrag({
    onStart: e => {
      this.dockedDragCluster = (
        e.currentTarget as HTMLElement
      ).closest<HTMLElement>('[data-docked-cluster]')
      this.dockedDragStart = {
        px: e.clientX,
        py: e.clientY,
        ox: this.dockedOffset.x,
        oy: this.dockedOffset.y,
      }
    },
    onMove: e => {
      if (!this.dockedDragStart) {
        return
      }
      this.dockedOffset = this.clampDockedOffset(
        this.dockedDragStart.ox + (e.clientX - this.dockedDragStart.px),
        this.dockedDragStart.oy + (e.clientY - this.dockedDragStart.py),
      )
      this.writeDockedPosition()
    },
    onEnd: () => {
      this.dockedDragStart = null
      this.dockedDragCluster = null
    },
  })

  public readonly startDockedDrag = (e: React.PointerEvent): void =>
    this.dockDrag.onPointerDown(e)

  // Clamp inside the MAP overlay, not the window — the overlay excludes the list
  // panel and doesn't clip the absolute cluster, so a window clamp lets the Dock
  // be dragged over the list. left = 50% + x under -50% translate → centre = ow/2 + x.
  private clampDockedOffset(x: number, y: number): { x: number; y: number } {
    const el = this.dockedDragCluster
    if (!el) {
      return { x, y }
    }
    const overlay = el.offsetParent as HTMLElement | null
    const m = DOCKED_TOOLBAR_MARGIN_PX
    const ow = overlay?.clientWidth ?? window.innerWidth
    const oh = overlay?.clientHeight ?? window.innerHeight
    const w = el.offsetWidth
    const h = el.offsetHeight
    const yMax = DOCKED_TOOLBAR_BOTTOM_PX - m
    const yMin = DOCKED_TOOLBAR_BOTTOM_PX - (oh - h - m)
    return {
      x: Math.min(Math.max(x, m - ow / 2 + w / 2), ow / 2 - m - w / 2),
      y: Math.min(Math.max(y, yMin), yMax),
    }
  }

  public centerPinInView(sheetTopY: number): void {
    this.session.centerCreationPinInView(sheetTopY)
  }

  // Every row opens through here; the write path differs per type, so each quirk is
  // kept rather than generalised away.
  private openFieldPicker(field: IPermitTypeField, anchor: HTMLElement): void {
    if (!field) {
      return
    }
    this.setPickerAnchor(anchor)

    // Dates write via applyDates, which bypasses the handler — and want no index.
    if (field.type === PermitFieldType.StartFinishDates) {
      this.formStore.setSelectedField(field)
      return
    }

    if (locationPermitFieldTypes.includes(field.type)) {
      // The pin owns preciseLocation, so the picker may only set the location tag.
      // The default write path replaces the whole value and would drop the pin's
      // coordinates, saving a workflow that no longer sits where it was drawn.
      this.formStore.setSelectedOnChangeHandler(<T>(newValue: T) => {
        const pin = this.getFieldValue<ISiteLocation>(field)?.preciseLocation
        const { workflowFieldsStore } = this.formStore
        // A multi-value pick hands back the whole set; the drawn pin stays on the first.
        if (Array.isArray(newValue)) {
          const [first, ...rest] = (newValue as ISiteLocation[]).filter(
            value => value?.id,
          )
          workflowFieldsStore.changeLocationFieldValues(
            field,
            first ? [{ ...first, preciseLocation: pin ?? null }, ...rest] : [],
          )
          return
        }
        workflowFieldsStore.changeFieldValue(field, 0, {
          ...(newValue as unknown as ISiteLocation),
          preciseLocation: pin ?? null,
        } as ISiteLocation)
      })
      this.formStore.setSelectedField(field, 0)
      return
    }

    if (userPermitFieldTypes.includes(field.type)) {
      this.formStore.setSelectedField(field, 0)
      // Apply hands back an id array; mirror PermitFormUserField.onApply — a multi
      // field stores the whole set, a single field takes the first.
      this.formStore.setSelectedOnChangeHandler(<T>(newValue: T) => {
        const userIds = (newValue as unknown as string[]) ?? []
        const { workflowFieldsStore } = this.formStore
        if (field.isMultiple) {
          workflowFieldsStore.changeMultipleFieldValues(field, userIds)
        } else {
          workflowFieldsStore.changeFieldValue(field, 0, userIds[0] || '')
        }
      })
      return
    }

    // Everything else stores through the default field-write path. Clearing the
    // handler matters: a custom one would mis-read an id string as an array and keep
    // only its first character.
    this.formStore.setSelectedOnChangeHandler(null)
    this.formStore.setSelectedField(field, 0)
  }

  public submit = async (): Promise<void> => {
    if (!this.canSubmit || this.isSubmitting) {
      return
    }
    // Lock up front: createPermit's validation chain is async — without this,
    // repeat taps during it create duplicate workflows.
    runInAction(() => {
      this.isSubmitting = true
      // Trim here, not while typing: a whitespace-only title is truthy and would
      // defeat the type-name fallback.
      const permit = this.formStore.editablePermit
      if (permit?.title) {
        permit.title = permit.title.trim()
      }
    })
    // Commit NOW, not after the async validation chain: the session turns 'submitting',
    // so a view switch mid-validation can't discard what the user just submitted.
    this.session.commitDrafts()
    let saveDispatched = false
    await this.formStore.createPermit(() => {
      saveDispatched = true
      this.handleSaveDispatched()
    })
    // Nothing dispatched (gate rejected/cancelled): release the button and reopen the
    // editor; the dispatched path resets via handleCreate{Success,Error}.
    if (!saveDispatched) {
      this.session.resumeDrawing()
      runInAction(() => {
        this.isSubmitting = false
      })
    }
  }

  private unregisterCapture(): void {
    if (this.captureRegistryKey) {
      this.formStore.unregisterPreciseLocationCapture(this.captureRegistryKey)
      this.captureRegistryKey = null
    }
  }

  private initDefaults(): void {
    // A re-seed would otherwise strand the old key on a template it no longer owns.
    this.unregisterCapture()
    const coordinates = this.session.creationCoordinates
    // Seed the defaults builder's location (same shape the right-click create uses).
    const preSelectedLocation = coordinates
      ? ({
          id: null,
          type: null,
          preciseLocation: {
            ...preciseLocationAtCoordinates(
              this.session.mapBoxViewerStore,
              coordinates,
            ),
            // The pin-drop camera, not the one the Composer happens to open under.
            ...(this.session.creationCamera ?? {}),
          },
        } as ISiteLocation)
      : null
    this.formStore.resetSelectedField()
    this.formStore.setDefaultSitePermit(
      null,
      this.session.creationWorkflowTypeId,
      false,
      null,
      preSelectedLocation,
    )
    this.formStore.openDeadlineModalIfNeed()
    const seeded = this.formStore.editablePermit
    this.seedDates = seeded
      ? {
          startDate: seeded.startDate,
          endDate: seeded.endDate,
          isAllDay: seeded.isAllDay,
        }
      : null

    // Register a screenshot capture callback so the logistics map is used
    // for PDF report images — the Composer doesn't mount the full form's
    // WorkflowFormPreciseLocation, so without this no capture happens.
    if (preSelectedLocation?.preciseLocation) {
      this.captureRegistryKey = `composer-${Date.now()}-${Math.random()}`
      this.formStore.registerPreciseLocationCapture(
        this.captureRegistryKey,
        this.captureAndApplyScreenshot,
      )
    }
  }

  private captureAndApplyScreenshot = async (): Promise<void> => {
    const field = this.locationField
    if (!field) return
    const location = this.getFieldValue<ISiteLocation>(field)
    const pl = location?.preciseLocation
    if (!hasPreciseCoordinates(pl)) return

    // Clear stale URLs — "no image" is safer than "wrong image"
    clearSnapshotUrls(location)

    const map = this.session.mapBoxViewerStore.mapRef?.getMap?.()
    if (!map) {
      toast.error(CAPTURE_FAILED_MESSAGE)
      return
    }

    const globe = this.session.mapBoxViewerStore.selectedGlobe

    // The capture moves the camera behind a still of the map, so the card must hold its place:
    // tracking the pin through it would leave the card as the only thing moving on screen.
    this.anchorController.freeze()
    let result: { imageUrl?: string; overviewImageUrl?: string } = {}
    try {
      result = await captureMapScreenshots({
        map,
        fileUploadingStore: this.deps.fileUploadingStore,
        projectId: this.deps.eventsStore.appState.activeProject.id,
        preciseViewport: {
          latitude: pl.latitude,
          longitude: pl.longitude,
          zoom: preciseLocationZoom(pl.zoom),
          bearing: pl.bearing ?? globe?.bearing ?? 0,
          pitch: pl.pitch ?? globe?.pitch ?? 0,
        },
        cropSize: { width: CAPTURE_WIDTH, height: CAPTURE_HEIGHT },
        isSatellite: this.session.mapBoxViewerStore.isSatelliteActive,
        onCameraRestored: () => this.anchorController.unfreeze(),
      })
    } finally {
      this.anchorController.unfreeze()
    }

    const merged = mergeSnapshotResult(pl, result)
    pl.imageUrl = merged.imageUrl
    pl.overviewImageUrl = merged.overviewImageUrl
    if (!merged.imageUrl) {
      toast.error(CAPTURE_FAILED_MESSAGE)
    }
  }

  // Location only, matching the seeder — a wider match addresses a different field.
  @computed
  private get locationField(): IPermitTypeField {
    return this.formStore.template?.locationField
  }

  private getFieldValue<T>(field: IPermitTypeField): T {
    return field
      ? this.formStore.workflowFieldsStore.getFieldValues<T>(field.id)?.[0]
      : undefined
  }

  // Adding a row seeds a null and every mapper dereferences its argument, so an
  // unfiltered read throws inside the composerRows computed mid-render.
  private getFieldValues<T>(field: IPermitTypeField): T[] {
    return field
      ? (
          this.formStore.workflowFieldsStore.getFieldValues<T>(field.id) ?? []
        ).filter(value => value !== null && value !== undefined)
      : []
  }

  @action.bound
  private setPickerAnchor(anchor: HTMLElement): void {
    this.pickerAnchor = anchor.getBoundingClientRect()
  }

  @action.bound
  private handleSaveDispatched(): void {
    this.isSubmitting = true
    // No-op on the direct path (submit() already committed); re-commits when a
    // validation gate deferred the create and resumeDrawing reopened the editor.
    this.session.commitDrafts()
  }

  private handleCreateSuccess = (permits: ISitePermit[]): void => {
    const main = permits[permits.length - 1]
    this.session.flushDraftsTo(main.id)
    runInAction(() => {
      this.isSubmitting = false
    })
    this.session.lastCreatedPermitId = main.id
    this.session.endCreation('complete')
    toast.success(
      Localization.translator.workflowTitleSubmitted(
        main.title || this.workflowType?.name,
      ),
    )
  }

  private handleCreateError = (): void => {
    runInAction(() => {
      this.isSubmitting = false
    })
    toast.error(Localization.translator.failedToCreateWorkflow)
    // A view switch is allowed mid-submit, which tears this store down with the
    // overlay — there is then no editor to reopen, so drop the buffer instead.
    if (this.isDisposed) {
      this.session.abandonSubmission()
      return
    }
    // Markups stay buffered under the temp id; reopen the editor for retry.
    this.session.resumeDrawing()
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    // The card lives on the map: Esc must not minimize it, nor Cmd+Enter submit,
    // from behind an overlay.
    if (!isBaseSurfaceActive()) {
      return
    }
    // A key another handler already consumed — e.g. the markup editor's Enter
    // finishing a polygon — must not also open the form or submit.
    if (e.defaultPrevented) {
      return
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      void this.submit()
      return
    }
    // Enter restores the minimized pill (its "Open ↵" affordance). Window-level,
    // so ignore Enter aimed at an input or control elsewhere on the page.
    if (this.isMinimized && e.key === 'Enter') {
      if (isEditableOrControlTarget(e.target)) return
      e.preventDefault()
      this.openWorkflowForm()
      return
    }
    if (e.key !== 'Escape' || this.isMinimized) {
      return
    }
    // The markup editor's Esc ladder marks handled events; a "free" Esc first closes
    // an open picker, then minimizes (footer Cancel discards markups via the guard).
    if (this.formStore.selectedField || this.formStore.isTypeSelectorShown) {
      this.formStore.closePicker()
      return
    }
    this.minimize()
  }
}
